import {
  Action,
  ActionType,
  FlowOperationType,
  flowStructureUtil,
  isFlowControlStep,
  isNil,
  Trigger,
  TriggerType,
} from '@activepieces/shared';
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useDebounce } from 'use-debounce';

import { SearchInput } from '../../../components/ui/search-input';
import { Checkbox } from '@/components/ui/checkbox';

import { AskAiButton } from './ask-ai';
import { PiecesCardList } from './pieces-card-list';
import { StepsCardList } from './steps-card-list';

import { useBuilderStateContext } from '@/app/builder/builder-hooks';
import { pieceSelectorUtils } from '@/app/builder/pieces-selector/piece-selector-utils';
import {
  PieceTagEnum,
  PieceTagGroup,
} from '@/app/builder/pieces-selector/piece-tag-group';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { UNSAVED_CHANGES_TOAST, toast } from '@/components/ui/use-toast';
import { piecesHooks } from '@/features/pieces/lib/pieces-hook';
import {
  StepMetadata,
  PieceSelectorOperation,
  HandleSelectCallback,
  StepMetadataWithSuggestions,
  PieceStepMetadata,
} from '@/features/pieces/lib/types';
import { platformHooks } from '@/hooks/platform-hooks';
import { t } from 'i18next';

type PieceSelectorProps = {
  children: React.ReactNode;
  open: boolean;
  asChild?: boolean;
  initialSelectedPiece?: string | undefined;
  onOpenChange: (open: boolean) => void;
} & { operation: PieceSelectorOperation };

type PieceGroup = {
  title: string;
  pieces: StepMetadataWithSuggestions[];
};

const PieceSelector = ({
  children,
  open,
  asChild = true,
  onOpenChange,
  operation,
  initialSelectedPiece,
}: PieceSelectorProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery] = useDebounce(searchQuery, 300);
  const [showOldVersions, setShowOldVersions] = useState(false);
  const [selectedPieceMetadata, setSelectedMetadata] = useState<
    StepMetadata | undefined
  >(undefined);

  const initiallySelectedMetaDataRef = useRef<StepMetadata | undefined>(
    undefined
  );

  const [selectedTag, setSelectedTag] = useState<PieceTagEnum>(
    PieceTagEnum.ALL
  );
  const [
    applyOperation,
    selectStepByName,
    flowVersion,
    setSampleData,
    setAskAiButtonProps,
    selectedStep,
  ] = useBuilderStateContext((state) => [
    state.applyOperation,
    state.selectStepByName,
    state.flowVersion,
    state.setSampleData,
    state.setAskAiButtonProps,
    state.selectedStep,
  ]);
  const isTrigger = operation.type === FlowOperationType.UPDATE_TRIGGER;
  const { metadata, isLoading: isLoadingPieces } =
    piecesHooks.useAllStepsMetadata({
      searchQuery: debouncedQuery,
      type: isTrigger ? 'trigger' : 'action',
      enabled: open,
    });

  const { platform } = platformHooks.useCurrentPlatform();

  const pieceGroups = useMemo(() => {
    if (!metadata) return [];

    const filteredMetadataOnTag = metadata.filter((stepMetadata) => {
      if (isFlowControlStep((stepMetadata as PieceStepMetadata).pieceName)) {
        return false;
      }
      switch (selectedTag) {
        case PieceTagEnum.CORE:
          return pieceSelectorUtils.isCorePiece(stepMetadata);
        case PieceTagEnum.AI:
          return pieceSelectorUtils.isAiPiece(stepMetadata);
        case PieceTagEnum.APPS:
          return pieceSelectorUtils.isAppPiece(stepMetadata);
        case PieceTagEnum.ALL:
        default:
          return true;
      }
    });

    const piecesMetadata =
      debouncedQuery.length > 0
        ? filterOutPiecesWithNoSuggestions(filteredMetadataOnTag)
        : filteredMetadataOnTag;

    initiallySelectedMetaDataRef.current = piecesMetadata.find(
      (p) => p.displayName === initialSelectedPiece
    );
    setSelectedMetadata(initiallySelectedMetaDataRef.current);

    if (debouncedQuery.length > 0 && piecesMetadata.length > 0) {
      return [{ title: 'Search Results', pieces: piecesMetadata }];
    }

    const flowControllerPieces = piecesMetadata.filter(
      (p) => pieceSelectorUtils.isFlowController(p) && !isTrigger
    );
    const universalAiPieces = piecesMetadata.filter(
      (p) => pieceSelectorUtils.isUniversalAiPiece(p) && !isTrigger
    );
    const utilityCorePieces = piecesMetadata.filter(
      (p) => pieceSelectorUtils.isUtilityCorePiece(p, platform) && !isTrigger
    );
    const popularPieces = piecesMetadata
      .filter(
        (p) =>
          pieceSelectorUtils.isPopularPieces(p, platform) &&
          selectedTag !== PieceTagEnum.AI,
      )
      .sort((a, b) => {
        return (
          pieceSelectorUtils.getPopularPiecePriority(
            (a as PieceStepMetadata).pieceName,
          ) -
          pieceSelectorUtils.getPopularPiecePriority(
            (b as PieceStepMetadata).pieceName,
          )
        );
      });

    const swaggerPieces = piecesMetadata.filter(
      (p) => pieceSelectorUtils.isSwaggerPiece(p) && !isTrigger
    );
    const other = piecesMetadata.filter(
      (p) =>
        !popularPieces.includes(p) &&
        !utilityCorePieces.includes(p) &&
        !flowControllerPieces.includes(p) &&
        !universalAiPieces.includes(p) &&
        !swaggerPieces.includes(p)
    );

    const groups: PieceGroup[] = [
      { title: t('Popular'), pieces: popularPieces },
      { title: t('Flow Control'), pieces: flowControllerPieces },
      { title: t('Utility'), pieces: utilityCorePieces },
      { title: 'Universal AI', pieces: universalAiPieces },
      { title: 'Swagger', pieces: swaggerPieces },
      { title: t('Other'), pieces: other },
    ];

    return groups.filter((group) => group.pieces.length > 0);
  }, [
    metadata,
    selectedTag,
    debouncedQuery,
    platform,
    isTrigger,
    initialSelectedPiece,
  ]);

  const piecesIsLoaded = !isLoadingPieces && pieceGroups.length > 0;
  const noResultsFound = !isLoadingPieces && pieceGroups.length === 0;

  const { listHeightRef, popoverTriggerRef, aboveListSectionHeight } =
    pieceSelectorUtils.useAdjustPieceListHeightToAvailableSpace(open);

  const resetField = () => {
    setSearchQuery('');
    setSelectedMetadata(initiallySelectedMetaDataRef.current);
    setSelectedTag(PieceTagEnum.ALL);
  };

  const handleSelect: HandleSelectCallback = (
    stepMetadata,
    actionOrTrigger
  ) => {
    resetField();
    onOpenChange(false);
    const newStepName = pieceSelectorUtils.getStepName(
      stepMetadata,
      flowVersion
    );

    const stepData = pieceSelectorUtils.getDefaultStep({
      stepName: newStepName,
      stepMetadata,
      actionOrTrigger,
      selectedStepName: selectedStep ?? '',
      flowVersion,
    });

    switch (operation.type) {
      case FlowOperationType.UPDATE_TRIGGER: {
        setSampleData(stepData.name, undefined);
        applyOperation(
          {
            type: FlowOperationType.UPDATE_TRIGGER,
            request: stepData as Trigger,
          },
          () => toast(UNSAVED_CHANGES_TOAST)
        );
        selectStepByName('trigger', 'user');
        break;
      }
      case FlowOperationType.ADD_ACTION: {
        applyOperation(
          {
            type: FlowOperationType.ADD_ACTION,
            request: {
              ...operation.actionLocation,
              action: stepData as Action,
              parentStep: [operation.actionLocation.parentStep],
              connection: operation.connection,
              newStepPosition: operation.newStepPosition,
            },
          },
          () => toast(UNSAVED_CHANGES_TOAST)
        );
        selectStepByName(stepData.name, 'user');
        break;
      }
      case FlowOperationType.UPDATE_ACTION: {
        const currentAction = flowStructureUtil.getStep(
          operation.stepName,
          flowVersion
        );
        if (isNil(currentAction)) {
          console.error(
            "Trying to update an action that's not in the displayed flow version"
          );
          return;
        }
        if (
          currentAction.type === TriggerType.EMPTY ||
          currentAction.type === TriggerType.PIECE
        ) {
          console.error(
            "Trying to update an action that's actually the trigger in the displayed flow version"
          );
          return;
        }
        if (
          (currentAction.type !== ActionType.PIECE &&
            stepData.type !== ActionType.PIECE &&
            stepData.type === currentAction.type) ||
          (currentAction.type === ActionType.PIECE &&
            stepData.type === ActionType.PIECE &&
            stepData.settings.actionName === currentAction.settings.actionName)
        ) {
          return;
        }

        applyOperation(
          {
            type: FlowOperationType.UPDATE_ACTION,
            request: {
              type: (stepData as Action).type,
              displayName: stepData.displayName,
              name: operation.stepName,
              skip: (stepData as Action).skip,
              settings: {
                ...stepData.settings,
              },
              valid: stepData.valid,
            },
          },
          () => toast(UNSAVED_CHANGES_TOAST)
        );
      }
    }
    setAskAiButtonProps(null);
  };
  const isMobile = pieceSelectorUtils.useIsMobile();
  return (
    <Popover
      open={open}
      modal={true}
      onOpenChange={(open) => {
        if (!open) {
          resetField();
        }
        onOpenChange(open);
      }}
    >
      <PopoverTrigger ref={popoverTriggerRef} asChild={asChild}>
        {children}
      </PopoverTrigger>
      <PopoverContent
        onContextMenu={(e) => {
          e.stopPropagation();
        }}
        className="w-[340px] md:w-[600px] p-0 shadow-lg z-[2000]"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <>
          <div
            style={{
              height: `${aboveListSectionHeight}px`,
            }}
          >
            <div className="p-2 flex gap-1 items-center relative">
              <SearchInput
                placeholder={t('Search')}
                value={searchQuery}
                showDeselect={searchQuery.length > 0}
                onChange={(e) => {
                  setSearchQuery(e);
                  setSelectedTag(PieceTagEnum.ALL);
                  setSelectedMetadata(undefined);
                }}
              />
              {operation.type !== FlowOperationType.UPDATE_TRIGGER && (
                <AskAiButton
                  varitant="ghost"
                  operation={operation}
                  onClick={() => {
                    onOpenChange(false);
                  }}
                ></AskAiButton>
              )}
              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-2 max-w-[45%]">
                <Checkbox
                  id="show-old-versions"
                  checked={showOldVersions}
                  onCheckedChange={(checked) =>
                    setShowOldVersions(checked as boolean)
                  }
                  className="flex-shrink-0"
                />
                <label
                  htmlFor="show-old-versions"
                  className="text-sm text-muted-foreground whitespace-nowrap overflow-hidden text-ellipsis cursor-pointer"
                >
                  {t('Show old versions')}
                </label>
              </div>
            </div>

            <PieceTagGroup
              selectedTag={selectedTag}
              type={
                operation.type === FlowOperationType.UPDATE_TRIGGER
                  ? 'trigger'
                  : 'action'
              }
              onSelectTag={(value) => {
                setSelectedTag(value);
                setSelectedMetadata(undefined);
              }}
            />
            <Separator orientation="horizontal" />
          </div>

          {!isMobile && (
            <div
              className=" flex   flex-row overflow-y-auto max-h-[300px] h-[300px] "
              style={{
                height: listHeightRef.current + 'px',
              }}
            >
              <PiecesCardList
                closePieceSelector={() => onOpenChange(false)}
                debouncedQuery={debouncedQuery}
                selectedTag={selectedTag}
                piecesIsLoaded={piecesIsLoaded}
                noResultsFound={noResultsFound}
                selectedPieceMetadata={selectedPieceMetadata}
                setSelectedMetadata={setSelectedMetadata}
                operation={operation}
                handleSelect={handleSelect}
                pieceGroups={pieceGroups}
                isLoadingPieces={isLoadingPieces}
                showOldVersions={showOldVersions}
                listHeight={listHeightRef.current}
              />

              {debouncedQuery.length === 0 &&
                piecesIsLoaded &&
                !noResultsFound && (
                  <>
                    <Separator orientation="vertical" className="h-full" />
                    <StepsCardList
                      selectedPieceMetadata={selectedPieceMetadata}
                      handleSelect={handleSelect}
                    />
                  </>
                )}
            </div>
          )}

          {isMobile && (
            <div
              className=" max-h-[300px] h-[300px]"
              style={{
                height: listHeightRef.current + 'px',
              }}
            >
              <PiecesCardList
                closePieceSelector={() => onOpenChange(false)}
                debouncedQuery={debouncedQuery}
                selectedTag={selectedTag}
                piecesIsLoaded={piecesIsLoaded}
                noResultsFound={noResultsFound}
                selectedPieceMetadata={selectedPieceMetadata}
                setSelectedMetadata={setSelectedMetadata}
                operation={operation}
                handleSelect={handleSelect}
                pieceGroups={pieceGroups}
                isLoadingPieces={isLoadingPieces}
                showOldVersions={showOldVersions}
                listHeight={listHeightRef.current}
              />
            </div>
          )}
        </>
      </PopoverContent>
    </Popover>
  );
};

export { PieceSelector };

function filterOutPiecesWithNoSuggestions(
  metadata: StepMetadataWithSuggestions[]
) {
  return metadata.filter((step) => {
    const isActionWithSuggestions =
      step.type === ActionType.PIECE &&
      step.suggestedActions &&
      step.suggestedActions.length > 0;
    const isTriggerWithSuggestions =
      step.type === TriggerType.PIECE &&
      step.suggestedTriggers &&
      step.suggestedTriggers.length > 0;
    const isNotPieceType =
      step.type !== ActionType.PIECE && step.type !== TriggerType.PIECE;

    return (
      isActionWithSuggestions || isTriggerWithSuggestions || isNotPieceType
    );
  });
}
